import { Rng } from "../core/rng";
import { TAU, dist2 } from "../core/math";
import { defaultStats, maxIntegrity, type PlayerStats } from "./stats";
import { STARTING_WEAPON, WEAPONS, type WeaponDef } from "../data/weapons";
import type {
  Beam,
  CommNode,
  Enemy,
  FloatingText,
  Particle,
  Pickup,
  Player,
  Projectile,
} from "./entities";
import { difficultyAt, DEFAULT_DIFFICULTY, type Difficulty } from "../data/difficulty";

export type Phase = "title" | "playing" | "levelup" | "paused" | "gameover" | "stageintro" | "lab";

export interface EquippedWeapon {
  def: WeaponDef;
  timer: number; // counts down to next activation
}

// Default arena size; overridden per-run to match the window aspect so the
// board fills the browser with no letterboxing.
export const DEFAULT_ARENA_W = 1500;
export const DEFAULT_ARENA_H = 1000;

/** Seconds you must defend a given zone (shorter, capped — session-length runs). */
export function zoneDurationFor(zone: number): number {
  return Math.min(30 + (zone - 1) * 3, 55);
}

/** Communication nodes to defend in a zone (capped so the objective stays losable). */
export function nodeCountForZone(zone: number): number {
  return Math.min(1 + zone, 6);
}

// Upgrade pacing: the first build-defining upgrade choice arrives at this level,
// then one every UPGRADE_LEVEL_INTERVAL levels. Other level-ups give only the
// small passive bonus, so picks stay meaningful instead of constant.
const FIRST_UPGRADE_LEVEL = 2;
const UPGRADE_LEVEL_INTERVAL = 3;

/** Seconds an uncollected drop (XP orb OR fragment) lingers before despawning. */
export const DROP_LIFETIME = 2.5;

// Single mutable container for an in-progress run. Systems read & mutate it.
export class GameState {
  rng: Rng;
  phase: Phase = "title";

  /** Arena (world) dimensions — set per run to match the window aspect. */
  arenaW: number;
  arenaH: number;

  player: Player;
  stats: PlayerStats;
  weapons: EquippedWeapon[] = [];

  enemies: Enemy[] = [];
  projectiles: Projectile[] = [];
  pickups: Pickup[] = [];
  nodes: CommNode[] = [];
  particles: Particle[] = [];
  floats: FloatingText[] = [];
  beams: Beam[] = [];

  // Resources
  bandwidth = 0; // XP toward next level
  level = 1;
  nextLevelAt = 10;
  fragments = 0;
  signal = 0;
  signalMax = 100;

  // Run meta
  time = 0; // seconds survived
  wave = 0; // ~minute index, drives spawn difficulty
  zone = 1; // current zone (network region)
  zoneDuration = 60; // seconds you must defend this zone
  zoneTimer = 60; // seconds remaining before the zone is restored
  kills = 0;
  firedThisFrame = false; // any weapon (auto or manual) fired this tick — drives the shoot SFX
  pendingLevelUps = 0; // queued upgrade choices (presented per level-up)
  bossEnemy: Enemy | null = null; // active boss (also stored in `enemies`)
  storyComplete = false; // Zero Day beaten at least once this run
  stageIntroIndex = 0; // which stage's story card to show in the "stageintro" phase

  // Settings (assigned per-run from saved Settings)
  difficulty: Difficulty = difficultyAt(DEFAULT_DIFFICULTY);
  screenShake = true;
  reduceFlashing = false;

  // Camera / juice
  camX = 0;
  camY = 0;
  shake = 0;
  damageFlash = 0; // red screen-edge flash when PCKT is hit (0..1)

  // Level-up choice state
  pendingChoices: import("../data/upgrades").UpgradeDef[] = [];
  selIndex = 0;

  // Upgrades taken this run, for the HUD stack display.
  upgradeStacks: { id: string; name: string; color: string; icon: string; count: number }[] = [];

  constructor(seed: number, arenaW = DEFAULT_ARENA_W, arenaH = DEFAULT_ARENA_H) {
    this.rng = new Rng(seed);
    this.arenaW = arenaW;
    this.arenaH = arenaH;
    this.stats = defaultStats();
    this.player = {
      x: arenaW / 2,
      y: arenaH / 2,
      vx: 0,
      vy: 0,
      radius: 18,
      integrity: maxIntegrity(this.stats),
      invuln: 0,
      facing: 0,
    };
    this.weapons.push({ def: WEAPONS[STARTING_WEAPON], timer: 0 });
    this.zoneDuration = zoneDurationFor(this.zone);
    this.zoneTimer = this.zoneDuration;
    this.spawnNodes(nodeCountForZone(this.zone)); // zone 1 -> 2 nodes
  }

  /** Begin a fresh zone: new random node layout, board cleared, PCKT repaired. */
  startZone(): void {
    this.enemies = [];
    this.projectiles = [];
    this.beams = [];
    this.nodes = [];
    this.bossEnemy = null;
    this.zoneDuration = zoneDurationFor(this.zone);
    this.zoneTimer = this.zoneDuration;
    this.spawnNodes(nodeCountForZone(this.zone)); // more nodes each zone (zone 1 -> 2)
    this.player.x = this.arenaW / 2;
    this.player.y = this.arenaH / 2;
    this.player.vx = 0;
    this.player.vy = 0;
    this.player.integrity = maxIntegrity(this.stats);
    this.player.invuln = 1.2;
  }

  private spawnNodes(n: number): void {
    // Fresh RANDOM layout each zone, kept within a defensible band around the
    // center (not the far corners), with a minimum spacing between nodes.
    const cx = this.arenaW / 2;
    const cy = this.arenaH / 2;
    const minDim = Math.min(this.arenaW, this.arenaH);
    const rMin = minDim * 0.18;
    const rMax = minDim * 0.4;
    const minSpacing = minDim * 0.22;
    for (let i = 0; i < n; i++) {
      let x = cx;
      let y = cy;
      for (let attempt = 0; attempt < 24; attempt++) {
        const a = this.rng.angle();
        const rr = this.rng.range(rMin, rMax);
        x = cx + Math.cos(a) * rr;
        y = cy + Math.sin(a) * rr;
        if (this.nodes.every((nd) => dist2(x, y, nd.x, nd.y) >= minSpacing * minSpacing)) break;
      }
      this.nodes.push({
        x,
        y,
        radius: 34,
        stability: 100,
        maxStability: 100,
        online: true,
        underAttack: 0,
        warned: false,
      });
    }
  }

  // ── helpers ───────────────────────────────────────────────────────────────

  nearestEnemy(x: number, y: number, maxDist: number, exclude?: Set<Enemy>): Enemy | null {
    let best: Enemy | null = null;
    let bestD = maxDist * maxDist;
    for (const e of this.enemies) {
      if (!e.alive || exclude?.has(e)) continue;
      const d = dist2(x, y, e.x, e.y);
      if (d < bestD) {
        bestD = d;
        best = e;
      }
    }
    return best;
  }

  nearestOnlineNode(x: number, y: number): CommNode | null {
    let best: CommNode | null = null;
    let bestD = Infinity;
    for (const n of this.nodes) {
      if (!n.online) continue;
      const d = dist2(x, y, n.x, n.y);
      if (d < bestD) {
        bestD = d;
        best = n;
      }
    }
    return best;
  }

  damageEnemy(e: Enemy, dmg: number, crit: boolean, kx = 0, ky = 0): void {
    if (e.bossState?.invulnerable) return; // scripted/intro bosses ignore damage
    e.hp -= dmg;
    e.hitFlash = 0.08;
    e.vx += kx;
    e.vy += ky;
    this.addFloat(e.x, e.y - e.radius, Math.round(dmg).toString(), crit ? "#e3c78a" : "#e4e4e4", crit ? 22 : 15);
    this.spawnParticles(e.x, e.y, e.def.color, crit ? 8 : 3, 160);
    if (e.hp <= 0) this.killEnemy(e);
  }

  killEnemy(e: Enemy, grantSignal = true): void {
    if (!e.alive) return;
    e.alive = false;
    this.kills++;
    this.spawnParticles(e.x, e.y, e.def.color, 14, 220);
    this.dropPickup(e.x, e.y, "bandwidth", e.def.xp);
    if (e.def.fragments > 0) this.dropPickup(e.x, e.y, "fragment", e.def.fragments);
    if (e.carrying > 0) this.dropPickup(e.x, e.y, "fragment", e.carrying);
    // Purge kills don't grant Signal, else the ultimate refunds its own charge.
    if (grantSignal) this.addSignal(2);
  }

  dropPickup(x: number, y: number, kind: Pickup["kind"], value: number): void {
    const a = this.rng.angle();
    const s = this.rng.range(20, 70);
    // All drops (XP orbs and fragments) despawn after a short time so the board
    // stays clean and you have to chase them.
    const life = DROP_LIFETIME;
    this.pickups.push({
      alive: true,
      x,
      y,
      vx: Math.cos(a) * s,
      vy: Math.sin(a) * s,
      kind,
      value,
      magnet: false,
      life,
      maxLife: life,
    });
  }

  addBandwidth(v: number): void {
    this.bandwidth += v;
    while (this.bandwidth >= this.nextLevelAt) {
      this.bandwidth -= this.nextLevelAt;
      this.level++;
      // steeper XP curve so leveling paces out over a run
      this.nextLevelAt = Math.floor(10 + this.level * 7 + this.level * this.level * 1.4);
      // Every level grants a small passive boost so leveling always feels good;
      // build-defining upgrade CHOICES are spaced out (see UPGRADE_LEVEL_*).
      this.applyLevelBonus();
      const upgradeReady =
        this.level >= FIRST_UPGRADE_LEVEL && (this.level - FIRST_UPGRADE_LEVEL) % UPGRADE_LEVEL_INTERVAL === 0;
      if (upgradeReady) {
        this.pendingLevelUps++; // game.ts presents a choose-3 for each queued upgrade
        this.addFloat(this.player.x, this.player.y - this.player.radius - 18, "UPGRADE READY", "#cf87e8", 19);
      } else {
        this.addFloat(this.player.x, this.player.y - this.player.radius - 18, "LEVEL UP", "#80a0ff", 16);
      }
    }
  }

  /** Small automatic stat gain on every level-up (independent of upgrade picks). */
  private applyLevelBonus(): void {
    this.stats.maxIntegrityAdd += 3;
    this.stats.damageMult += 0.02;
    // Top the player up by the integrity just gained.
    this.player.integrity = Math.min(maxIntegrity(this.stats), this.player.integrity + 3);
  }

  addFragments(v: number): void {
    this.fragments += v;
  }

  addSignal(v: number): void {
    this.signal = Math.min(this.signalMax, this.signal + v);
  }

  spawnParticles(x: number, y: number, color: string, count: number, speed: number): void {
    for (let i = 0; i < count; i++) {
      const a = this.rng.angle();
      const s = this.rng.range(speed * 0.3, speed);
      this.particles.push({
        x,
        y,
        vx: Math.cos(a) * s,
        vy: Math.sin(a) * s,
        life: this.rng.range(0.25, 0.6),
        maxLife: 0.6,
        size: this.rng.range(1.5, 3.5),
        color,
      });
    }
  }

  ring(x: number, y: number, color: string, count: number, speed: number): void {
    for (let i = 0; i < count; i++) {
      const a = (i / count) * TAU;
      this.particles.push({
        x,
        y,
        vx: Math.cos(a) * speed,
        vy: Math.sin(a) * speed,
        life: 0.5,
        maxLife: 0.5,
        size: 2.5,
        color,
      });
    }
  }

  addFloat(x: number, y: number, text: string, color: string, size: number): void {
    this.floats.push({ x, y, vy: -42, life: 0.7, text, color, size });
  }

  addBeam(x1: number, y1: number, x2: number, y2: number, color: string, width = 3, life = 0.12): void {
    this.beams.push({ x1, y1, x2, y2, life, maxLife: life, width, color });
  }

  addShake(amount: number): void {
    if (!this.screenShake) return; // accessibility: screen-shake toggle
    this.shake = Math.min(28, this.shake + amount);
  }
}
